/** Growth UX API: onboarding diagnostics, achievements, weekly report preview. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ApiError } from "./client";

export type DiagnosticSeverity = "ok" | "info" | "warning" | "error";

export interface DiagnosticItem {
  code: string;
  title: string;
  description: string;
  severity: DiagnosticSeverity;
  action_label: string | null;
  action_url: string | null;
}

export interface DiagnosticsResponse {
  score: number;
  summary: string;
  items: DiagnosticItem[];
  next_best_action: DiagnosticItem | null;
}

export interface AchievementItem {
  code: string;
  title: string;
  description: string;
  status: "locked" | "unlocked";
  unlocked_at: string | null;
}

export interface AchievementEvaluationResponse {
  items: AchievementItem[];
  unlocked_count: number;
  total_count: number;
  new_count: number;
  reward_days_granted: number;
  next_reward_in: number;
  subscription_expires_at: string | null;
}

export interface WeeklyReportPreview {
  kind: string;
  dedupe_key: string;
  text: string;
}

export function useDiagnostics() {
  return useQuery<DiagnosticsResponse, ApiError>({
    queryKey: ["growth", "diagnostics"],
    queryFn: () => api.get<DiagnosticsResponse>("/growth/diagnostics"),
    staleTime: 20_000,
  });
}

export function useAchievements() {
  return useQuery<AchievementEvaluationResponse, ApiError>({
    queryKey: ["growth", "achievements"],
    queryFn: () => api.get<AchievementEvaluationResponse>("/growth/achievements"),
    staleTime: 30_000,
  });
}

export function useCheckAchievements() {
  const qc = useQueryClient();
  return useMutation<AchievementEvaluationResponse, ApiError, void>({
    mutationFn: () => api.post<AchievementEvaluationResponse>("/growth/achievements/check", {}),
    onSuccess: (data) => {
      qc.setQueryData(["growth", "achievements"], data);
      qc.invalidateQueries({ queryKey: ["billing", "subscription"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useWeeklyReportPreview() {
  return useQuery<WeeklyReportPreview, ApiError>({
    queryKey: ["growth", "weekly-report", "preview"],
    queryFn: () => api.get<WeeklyReportPreview>("/growth/weekly-report/preview"),
    staleTime: 60_000,
  });
}
